// src/core/runtime.ts
// Runtime — wires together all subsystems + telemetry.
//
// Usage:
//   const runtime = initRuntime({ wsUrl: 'ws://localhost:8000/ws/ui' });
//   runtime.start();

import { WSClient } from '../api/wsClient';
import { FrameCache } from './frame_cache';
import { useUIFrameStore } from '../store/uiFrameStore';
import { useRankingStore } from '../store/rankingStore';
import { ReplayApi } from '../api/replayApi';
import { FrameMetrics } from '../telemetry/frame_metrics';
import { WSMetrics } from '../telemetry/ws_metrics';

export interface RuntimeConfig {
  wsUrl: string;
  maxFrames?: number;
  maxAgeMs?: number;
}

export interface Runtime {
  wsClient: WSClient;
  frameCache: FrameCache;
  replayApi: ReplayApi;
  frameMetrics: FrameMetrics;
  wsMetrics: WSMetrics;
  start: () => void;
  stop: () => void;
  reconnect: () => void;
}

export function createRuntime(config: RuntimeConfig): Runtime {
  const { wsUrl, maxFrames = 100_000, maxAgeMs = 30 * 60 * 1000 } = config;

  // 1. Create telemetry first (no deps)
  const frameMetrics = new FrameMetrics();
  const wsMetrics = new WSMetrics();

  // 2. Create FrameCache
  const frameCache = new FrameCache({ maxFrames, maxAgeMs });

  // 3. Create WSClient
  const wsClient = new WSClient({ url: wsUrl, frameCache });

  // 4. Wire wsClient frame events → store + telemetry
  wsClient.on('frame', (frame, meta) => {
    useUIFrameStore.getState().onFrame(frame);

    // Track frame ingestion
    const frameSize = JSON.stringify(frame.data).length;
    frameMetrics.onFrame(frameSize);

    if (frame.isDuplicate) {
      frameMetrics.onDropped();
    }
  });

  // 5. Wire wsClient status → store + telemetry
  wsClient.on('status', (status) => {
    useUIFrameStore.getState().onConnectionStatus(status);
    useRankingStore.getState().setConnectionStatus(status);
    wsMetrics.onStatusChange(status);
  });

  // 6. Wire wsClient rank_changed → RankingStore
  wsClient.on('rank_changed', (event) => {
    useRankingStore.getState().apply(event);
  });

  // 8. Init store to subscribe to FrameCache
  const unsubscribe = useUIFrameStore.getState()._init(frameCache);

  // 9. Create ReplayApi
  const replayApi = new ReplayApi({ frameCache });

  return {
    wsClient,
    frameCache,
    replayApi,
    frameMetrics,
    wsMetrics,
    start: () => wsClient.connect(),
    stop: () => {
      wsClient.disconnect();
      unsubscribe();
    },
    reconnect: () => {
      wsClient.disconnect();
      wsClient.connect();
    },
  };
}

// Singleton runtime instance
let _runtime: Runtime | null = null;

export function getRuntime(): Runtime | null {
  return _runtime;
}

export function initRuntime(config: RuntimeConfig): Runtime {
  if (_runtime) {
    console.warn('[runtime] Already initialized, returning existing instance');
    return _runtime;
  }
  _runtime = createRuntime(config);
  return _runtime;
}
